import { useState } from 'react';
import { inviteTeamMember, createCompanyPages as createCompanyPagesApi } from 'team/apis/teamMemberApi';
import { useAuth } from 'contexts/AuthContext';

export const useCreateTeamMember = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { authState } = useAuth();

    const createTeamMember = async (email) => {
        setLoading(true);
        setError(null);

        try {
            const teamId = authState?.user?.teamId;

            if (!teamId) {
                throw new Error('No team ID found. Please ensure you are part of a team.');
            }

            const memberData = {
                email: email.trim().toLowerCase(),
                role: 'member'
            };

            const result = await inviteTeamMember(teamId, memberData);
            return result;
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Failed to invite team member';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const createMultipleTeamMembers = async (emails, sendInvites = true) => {
        setLoading(true);
        setError(null);

        try {
            const teamId = authState?.user?.teamId;

            if (!teamId) {
                throw new Error('No team ID found. Please ensure you are part of a team.');
            }

            // ✅ sendInvites parameter controls whether welcome emails are sent
            // - true: Sends welcome email with login credentials/invitation link
            // - false: Creates team member without sending email
            const results = await Promise.allSettled(
                emails.map(email =>
                    inviteTeamMember(teamId, {
                        email: email.trim().toLowerCase(),
                        role: 'member',
                        sendInvites: sendInvites // ✅ Pass sendInvites to backend
                    })
                )
            );

            const successful = results
                .filter(result => result.status === 'fulfilled')
                .map(result => result.value);

            const failed = results
                .filter(result => result.status === 'rejected')
                .map((result, index) => {
                    console.log('❌ Failed invitation:', {
                        email: emails[index],
                        reason: result.reason,
                        response: result.reason?.response,
                        data: result.reason?.response?.data,
                        message: result.reason?.response?.data?.message || result.reason?.message
                    });
                    return {
                        email: emails[index],
                        error: result.reason?.response?.data?.message || result.reason?.message || 'Unknown error'
                    };
                });

            // ✅ Set error if some failed
            if (failed.length > 0) {
                const errorMsg = `${failed.length} of ${emails.length} invitation(s) failed`;
                setError(errorMsg);
            }

            return {
                successful,
                failed,
                successCount: successful.length,  // ✅ Added
                failedCount: failed.length,       // ✅ Added
                totalCount: emails.length          // ✅ Added
            };
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Failed to invite team members';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const createCompanyPages = async (count) => {
        setLoading(true);
        setError(null);

        try {
            const teamId = authState?.user?.teamId;

            if (!teamId) {
                throw new Error('No team ID found. Please ensure you are part of a team.');
            }

            return await createCompanyPagesApi(teamId, count);
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Failed to create company pages';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    return {
        createTeamMember,
        createMultipleTeamMembers,
        createCompanyPages,
        loading,
        error,
        clearError: () => setError(null)
    };
};